import { css, jsx } from '@emotion/core';
import { Pagination, Radio } from 'antd';
import moment from 'moment';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';

import Heading from '@/src/shared/components/Heading';
import Loading from '@/src/shared/components/LoadingResults';
import ResultCard from '@/src/shared/components/ResultCard';
import { colors, space } from '@/styles';
import formatURL from '@/utils/formatURL';

const styles = {
  paginationWrapper: css`
    display: flex;
    align-items: center;
    .ant-pagination-total-text {
      font-weight: bold;
      color: ${colors.secondaryGrey};
      font-family: 'Lato', sans-serif;
      font-size: 1.2rem;
    }
  `,
  results: css`
    font-weight: 900;
    font-size: 1.2rem;
    color: ${colors.secondaryGray};
  `,
  listWrapper: css`
    width: 100%;
    max-width: 100%;
  `,
  toggleWrapper: css`
    margin: 0 0 2.4rem;

    .ant-radio-group {
      font-size: 1.2rem;
      font-weight: 900;
    }

    .ant-radio-group-solid
      .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled) {
      background: ${colors.secondaryBlue};
      border-color: ${colors.secondaryBlue};

      &:hover {
        background: ${colors.secondaryBlueDark};
        border-color: ${colors.secondaryBlueDark};
      }
    }

    .ant-radio-button-wrapper {
      color: ${colors.secondaryBlue};

      &:hover {
        color: ${colors.secondaryBlueDark};
      }
    }
  `,
};

const PAGE_SIZE = 10;

const EventsList = props => {
  const { eventsData, loading } = props;
  const [currentPage, setCurrentPage] = useState(1);
  const [show, setShow] = useState('upcoming');

  const router = useRouter();

  const pastEvents = [];
  const upcomingEvents = [];
  if (eventsData.length) {
    const today = moment().startOf('day');
    eventsData.forEach(e => {
      const startDate = moment(e.MeetingStartDate);
      if (startDate.isBefore(today)) {
        pastEvents.push(e);
      } else {
        upcomingEvents.push(e);
      }
    });
  }

  useEffect(() => {
    const { page } = router.query;

    if (page && !Number.isNaN(page)) {
      setCurrentPage(page);
    }
  }, [router.query.page]);

  useEffect(() => {
    const { show: showValue } = router.query;

    setShow(showValue === 'past' ? 'past' : 'upcoming');
  }, [router.query.show]);

  const handleToggleChange = e => {
    const {
      target: { value },
    } = e;
    setCurrentPage(1);
    const newURL = formatURL(router.asPath, { show: value });
    router.replace(newURL, newURL, { shallow: true });
  };

  function handlePaginationChange(page) {
    setCurrentPage(page);
    const newURL = formatURL(router.asPath, { page });
    router.replace(newURL, newURL, { shallow: true });
  }

  function renderEvents() {
    const eventsArray = Object.values(show === 'past' ? pastEvents : upcomingEvents);
    const paginatedResults = eventsArray.slice(
      (currentPage - 1) * PAGE_SIZE,
      currentPage * PAGE_SIZE
    );
    const eventResults = {};
    for (let i = 0; i < paginatedResults.length; i += 1) {
      const result = paginatedResults[i];
      const resultEvent = result.ViewGroup;
      if (resultEvent in eventResults) {
        eventResults[resultEvent].push(result);
      } else {
        eventResults[resultEvent] = [result];
      }
    }

    return Object.entries(eventResults).map(([viewGroup, _results]) => {
      return (
        <React.Fragment key={viewGroup}>
          {/* <Heading type={3} weight="bold" theme="primaryGray" css={[space.m.t20, space.m.b20]}>
            {viewGroup}
          </Heading> */}
          {_results.map(result => (
            <ResultCard
              key={result.SitePageId}
              as={`/events/meetings/meeting/${result.SitePageId}`}
              href="/events/meetings/meeting/[slug]"
              title={result.PageTitle}
              subtitle={
                result.MeetingLocationId !== 1
                  ? moment(result.MeetingStartDate).format('M-DD-YYYY')
                  : ''
              } // Temp fix. AP-3483
              iconId={result.IconId}
              summary={result.ExecutiveSummary}
              truncate
              fullWidth
              summaryLimit={250}
            />
          ))}
        </React.Fragment>
      );
    });
  }

  function renderPagination(hideIfResults) {
    const eventCount = show === 'past' ? pastEvents.length : upcomingEvents.length;
    if (hideIfResults && !eventCount) return null;

    return (
      <div css={[space.m.b24, styles.paginationWrapper]}>
        <Pagination
          total={eventCount}
          pageSize={PAGE_SIZE}
          onChange={handlePaginationChange}
          showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
          current={Number(currentPage)}
        />
      </div>
    );
  }

  return (
    <div css={styles.listWrapper}>
      {loading ? (
        <Loading height="500px" />
      ) : (
        <>
          <div css={styles.toggleWrapper}>
            <Radio.Group defaultValue={show} buttonStyle="solid" onChange={handleToggleChange}>
              <Radio.Button value="upcoming">Upcoming Events</Radio.Button>
              <Radio.Button value="past">Past Events</Radio.Button>
            </Radio.Group>
          </div>

          {renderPagination()}

          <div>{renderEvents()}</div>

          {renderPagination(true)}
        </>
      )}
    </div>
  );
};

export default EventsList;
